import type { BulkData } from '../../products/domain/product';
import { StorageAreaStatus } from './storageAreaStatus';
import { StorageAreaType } from './storageAreaType';

export interface StorageArea {
  id: string;
  name: string;
  type: StorageAreaType;
  status: StorageAreaStatus;
  active: boolean;
  temporary: boolean;
  version: string | null;
  updatedAtUtc: string;
  logYardData: LogYardData | null;
  coilStack: CoilStack | null;
  bulkStorageData: BulkStorageData | null;
  packageStorageData: PackageStorageData | null;
  productionLineData: ProductionLineData | null;
  packagePlaceData: PackagePlaceData | null;
  note: string | null;
  orderName: string | null;
  importantNote: boolean;
  displayText: string | null;
}

export interface LogYardData {
  lastEmptyUtc: string;
  lastInventoryUtc: string | null;
  firstMoveToUtc: string | null;
  sideArea: number;
  allowMultipleProducts: boolean;
  disableAutomaticProductChange: boolean;
  content: LogYardContent[];
  allowedProducts: string[];
}

export interface LogYardContent {
  productId: string;
  numberOfLogs: number;
  volume: number;
}

export interface CoilStack {
  slots: number;
  slotWidth: number;
  maxLevels: number | null;
  maxHeight: number | null;
  maxCoilDiameter: number | null;
}

export interface BulkStorageData {
  lastEmptyUtc: string;
  lastInventoryUtc: string | null;
  firstMoveToUtc: string | null;
  maxWeight: number | null;
  maxVolume: number | null;
  allowMultipleProducts: boolean;
  disableAutomaticProductChange: boolean;
  content: BulkContent[];
  allowedProducts: string[];
}

export interface BulkContent {
  productId: string;
  ton: number;
}

export interface PackageStorageData {
  /** Default place length in millimeters. */
  defaultPlaceLength: number;
  /** Default place width in millimeters. */
  defaultPlaceWidth: number;
  defaultMaxStackCount: number | null;
  /** Default maximum weight in tonnes. */
  defaultMaxWeight: number | null;
  /** Default maximum height in millimeters. */
  defaultMaxHeight: number | null;
  places: StoragePlace[];
}

export interface StoragePlace {
  id: string;
  index: number;
  /** X position in millimeters, relative to the storage area origin. */
  x: number;
  /** Y position in millimeters, relative to the storage area origin. */
  y: number;
  /** Length (Y extent) in millimeters. */
  length: number;
  /** Width (X extent) in millimeters. */
  width: number;
  maxStackCount: number | null;
  /** Maximum weight in tonnes. */
  maxWeight: number | null;
  /** Maximum height in millimeters. */
  maxHeight: number | null;
  disabled: boolean;
  accessDirections: number;
  /** Stacking level; not below the storage area's z-index. Same-level places may not overlap. */
  zIndex: number;
  /** Column position within a grid layout (1-based). Null when not assigned to a grid. */
  column: number | null;
  /** Row position within a grid layout (1-based). Null when not assigned to a grid. */
  row: number | null;
  /** Score decrement applied per blocking package when evaluating accessibility. */
  accessibilityScoreDecrementPerBlocker: number;
  /** Computed display text, evaluated from the global place display text formula. */
  displayText: string | null;
}

export interface ProductionLineData {
  simulationEnabled: boolean;
  simulationIntervalSeconds: number | null;
  simulationOutputPlaceId: string | null;
  simulationProductId: string | null;
  simulationPiecesCount: number | null;
  simulationPackagingId: string | null;
  simulationPackageTypeId: string | null;
  simulationLength: number | null;
  simulationWidth: number | null;
  simulationHeight: number | null;
}

export interface PackagePlaceData {
  productionLineId: string | null;
  /** Default place length in millimeters. */
  defaultPlaceLength: number;
  /** Default place width in millimeters. */
  defaultPlaceWidth: number;
  defaultMaxStackCount: number | null;
  /** Default maximum weight in tonnes. */
  defaultMaxWeight: number | null;
  /** Default maximum height in millimeters. */
  defaultMaxHeight: number | null;
  places: StoragePlace[];
}

export function createStorageArea(id: string): StorageArea {
  return {
    id: id,
    active: true,
    coilStack: null,
    name: '',
    logYardData: null,
    bulkStorageData: null,
    packageStorageData: null,
    productionLineData: null,
    packagePlaceData: null,
    note: '',
    status: StorageAreaStatus.Empty,
    temporary: false,
    type: StorageAreaType.UnsortedPile,
    updatedAtUtc: '',
    version: null,
    orderName: null,
    importantNote: false,
    displayText: null,
  };
}

export function createLogYard(): LogYardData {
  const model: LogYardData = {
    lastEmptyUtc: new Date().toJSON(),
    lastInventoryUtc: null,
    firstMoveToUtc: null,
    sideArea: 0,
    allowMultipleProducts: false,
    disableAutomaticProductChange: false,
    content: [],
    allowedProducts: [],
  };

  return model;
}

export function createBulkStorageData(): BulkStorageData {
  const model: BulkStorageData = {
    lastEmptyUtc: new Date().toJSON(),
    lastInventoryUtc: null,
    firstMoveToUtc: null,
    maxWeight: null,
    maxVolume: null,
    allowMultipleProducts: false,
    disableAutomaticProductChange: false,
    content: [],
    allowedProducts: [],
  };

  return model;
}

export function createPackageStorageData(): PackageStorageData {
  const model: PackageStorageData = {
    defaultPlaceLength: 0,
    defaultPlaceWidth: 0,
    defaultMaxStackCount: null,
    defaultMaxWeight: null,
    defaultMaxHeight: null,
    places: [],
  };

  return model;
}
